import {
  collection,
  doc,
  setDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  arrayUnion,
} from "firebase/firestore";
import { db } from "../firebase";

const SUBS = "submissions";
const CASES = "cases";

export function generateSubmissionId() {
  // Local id generation (no network) so we can upload files under submissions/{caseId}/{submissionId}/...
  return doc(collection(db, SUBS)).id;
}

export async function listSubmissions(caseId) {
  const q = query(collection(db, SUBS), where("caseId", "==", caseId));
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => {
      const data = d.data();
      return { id: d.id, ...data, _ts: data.createdAt?.toMillis?.() ?? null };
    })
    .sort((a, b) => (a.version || 0) - (b.version || 0));
}

// Selected designer creates a NEW immutable version and moves the case to "Submitted".
export async function createSubmission(caseId, designerId, submissionId, { files, notes, intent }) {
  const existing = await listSubmissions(caseId);
  const version = existing.reduce((m, s) => Math.max(m, s.version || 0), 0) + 1;
  await setDoc(doc(db, SUBS, submissionId), {
    id: submissionId,
    caseId,
    designerId,
    version,
    files: files || [],
    notes: notes || "",
    intent: intent === "final" ? "final" : "review",
    createdAt: serverTimestamp(),
  });
  // Designer may only touch status/submissionFiles/submissionNotes/updatedAt on the case.
  await updateDoc(doc(db, CASES, caseId), { status: "Submitted", updatedAt: Date.now() });
  return version;
}

// Lab requests changes -> case goes back to "In Progress" so the designer can submit a new version.
export async function requestChanges(caseId) {
  await updateDoc(doc(db, CASES, caseId), { status: "In Progress", updatedAt: Date.now() });
}

// Lab approves a specific version (marks it approved) and completes the case.
// Only the owner can reach this (UI gated + Firestore rules); we record a simple
// audit trail (who/when) and a Submitted -> Completed transition entry.
export async function approveVersion(caseId, version, approver = {}) {
  const now = Date.now();
  const by = approver.uid || null;
  const byName = approver.name || null;
  await updateDoc(doc(db, CASES, caseId), {
    status: "Completed",
    approvedVersion: version,
    approvedBy: by,
    approvedByName: byName,
    approvedAt: now,
    completedBy: by,
    completedAt: now,
    updatedAt: now,
    statusHistory: arrayUnion({
      from: "Submitted",
      to: "Completed",
      action: "approve",
      version,
      by,
      byName,
      at: now,
    }),
  });
}
